import {useCallback, useEffect} from 'react';

import {Body} from 'shells/typography/body';

import type {AvailableField} from '../graphql/hooks/use-lead-research-prompt';

import {FieldToggleButton} from './settings/field-toggle-button';
import {SettingsTextSection} from './settings/settings-text-section';

import styles from './lead-researcher-settings.module.css';

type Props = {
    selectedFields: string[];
    businessContext: string;
    idealCustomer: string;
    availableFields: AvailableField[];
    onSelectedFieldsChange: (fields: string[]) => void;
    onBusinessContextChange: (value: string) => void;
    onIdealCustomerChange: (value: string) => void;
    onDirtyChange: (isDirty: boolean) => void;
    originalSelectedFields: string[];
    originalBusinessContext: string;
    originalIdealCustomer: string;
};

export function LeadResearcherSettings(props: Props) {
    const {
        selectedFields,
        businessContext,
        idealCustomer,
        availableFields,
        onSelectedFieldsChange,
        onBusinessContextChange,
        onIdealCustomerChange,
        onDirtyChange,
        originalSelectedFields,
        originalBusinessContext,
        originalIdealCustomer,
    } = props;

    useEffect(() => {
        const isDirty = checkSettingsDirty({
            selectedFields,
            businessContext,
            idealCustomer,
            originalSelectedFields,
            originalBusinessContext,
            originalIdealCustomer,
        });
        onDirtyChange(isDirty);
    }, [
        selectedFields,
        businessContext,
        idealCustomer,
        originalSelectedFields,
        originalBusinessContext,
        originalIdealCustomer,
        onDirtyChange,
    ]);

    const handleFieldToggle = useCallback(
        (fieldKey: string) => {
            const isSelected = selectedFields.includes(fieldKey);
            if (isSelected) {
                onSelectedFieldsChange(selectedFields.filter((f) => f !== fieldKey));
            } else {
                onSelectedFieldsChange([...selectedFields, fieldKey]);
            }
        },
        [selectedFields, onSelectedFieldsChange]
    );

    return (
        <div className={`flex flex-dir-col gap-24 overflow-y-auto ${styles['settings-content']}`}>
            <SettingsTextSection
                label='Describe your business'
                subtitle='The more detail you can provide, the more accurate the research will be'
                value={businessContext}
                onChange={onBusinessContextChange}
                placeholder='e.g. We help B2B teams manage and research leads so they can prioritize the right opportunities and follow up faster'
            />
            <SettingsTextSection
                label='Describe your ideal buyer'
                subtitle='What kind of customer should our AI prioritize when researching leads?'
                value={idealCustomer}
                onChange={onIdealCustomerChange}
                placeholder='e.g. B2B companies that manage lead qualification and pipeline strategy and want better tools to improve prospect research and lead quality'
            />
            <div className={styles.section}>
                <Body size='small' color='medium' isBold={true}>
                    Which company data matters most?
                </Body>
                <div className={styles['fields-grid']}>
                    {availableFields.map((field) => (
                        <FieldToggleButton
                            key={field.key}
                            label={field.label}
                            isSelected={selectedFields.includes(field.key)}
                            onClick={() => handleFieldToggle(field.key)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

/**
 * Determines if any settings have been modified from their original values.
 * Compares selected fields (order-independent), business context, and ideal customer.
 */
function checkSettingsDirty(params: {
    selectedFields: string[];
    businessContext: string;
    idealCustomer: string;
    originalSelectedFields: string[];
    originalBusinessContext: string;
    originalIdealCustomer: string;
}): boolean {
    const {
        selectedFields,
        businessContext,
        idealCustomer,
        originalSelectedFields,
        originalBusinessContext,
        originalIdealCustomer,
    } = params;

    const fieldsChanged =
        selectedFields.length !== originalSelectedFields.length ||
        selectedFields.some((f) => !originalSelectedFields.includes(f));
    const businessChanged = businessContext !== originalBusinessContext;
    const customerChanged = idealCustomer !== originalIdealCustomer;

    return fieldsChanged || businessChanged || customerChanged;
}
